/**
 * Calendar/time parsing for rule values. Zone math (wall clock, local → UTC, transitions) is
 * delegated to `@signetix/schedule-engine` and its pluggable ZoneProvider, so the rule engine
 * shares the schedule engine's tz semantics (SCHEDULING.md §4) and its bundled tzdb provider.
 *
 * Day numbers are days since 1970-01-01 of the local calendar; times are ms after local midnight.
 */
import { isKnownZone, toIso, toMs, wallClock } from '@signetix/schedule-engine';
export { isKnownZone, toIso, toMs, wallClock };
export const MS_PER_DAY = 86400000;
function floorDiv(a, b) {
    return Math.floor(a / b);
}
/** Days since 1970-01-01 of a proleptic Gregorian date (Howard Hinnant's days_from_civil). */
export function daysFromCivil(y, m, d) {
    const yy = m <= 2 ? y - 1 : y;
    const era = floorDiv(yy, 400);
    const yoe = yy - era * 400;
    const mp = (m + 9) % 12;
    const doy = floorDiv(153 * mp + 2, 5) + d - 1;
    const doe = yoe * 365 + floorDiv(yoe, 4) - floorDiv(yoe, 100) + doy;
    return era * 146097 + doe - 719468;
}
/** Inverse of `daysFromCivil`. */
export function civilFromDays(z0) {
    const z = z0 + 719468;
    const era = floorDiv(z, 146097);
    const doe = z - era * 146097;
    const yoe = floorDiv(doe - floorDiv(doe, 1460) + floorDiv(doe, 36524) - floorDiv(doe, 146096), 365);
    const doy = doe - (365 * yoe + floorDiv(yoe, 4) - floorDiv(yoe, 100));
    const mp = floorDiv(5 * doy + 2, 153);
    const d = doy - floorDiv(153 * mp + 2, 5) + 1;
    const m = mp < 10 ? mp + 3 : mp - 9;
    return { y: yoe + era * 400 + (m <= 2 ? 1 : 0), m, d };
}
function daysInMonth(y, m) {
    if (m === 2)
        return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28;
    return m === 4 || m === 6 || m === 9 || m === 11 ? 30 : 31;
}
function validYmd(y, m, d) {
    return m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m);
}
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** `YYYY-MM-DD` → day number, or null (malformed / non-existent date). */
export function parseDate(s) {
    const m = DATE_RE.exec(s);
    if (!m)
        return null;
    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    return validYmd(y, mo, d) ? daysFromCivil(y, mo, d) : null;
}
function pad(n, w) {
    let s = String(n);
    while (s.length < w)
        s = '0' + s;
    return s;
}
export function formatDay(day) {
    const c = civilFromDays(day);
    return `${pad(c.y, 4)}-${pad(c.m, 2)}-${pad(c.d, 2)}`;
}
/** ISO weekday 1 = Monday … 7 = Sunday (1970-01-01 was a Thursday). */
export function isoWeekday(day) {
    return ((((day + 3) % 7) + 7) % 7) + 1;
}
const TIME_RE = /^(\d{2}):(\d{2})(?::(\d{2}))?$/;
/** `HH:mm[:ss]` → ms after midnight; `24:00[:00]` only when `allow24`. Null when malformed. */
export function parseTime(s, allow24 = false) {
    const m = TIME_RE.exec(s);
    if (!m)
        return null;
    const h = +m[1];
    const mi = +m[2];
    const sec = m[3] === undefined ? 0 : +m[3];
    if (allow24 && h === 24 && mi === 0 && sec === 0)
        return MS_PER_DAY;
    if (h > 23 || mi > 59 || sec > 59)
        return null;
    return ((h * 60 + mi) * 60 + sec) * 1000;
}
/** ms after midnight → `HH:mm:ss` (milliseconds dropped). */
export function formatTime(ms) {
    const s = Math.floor(ms / 1000);
    return `${pad(Math.floor(s / 3600), 2)}:${pad(Math.floor(s / 60) % 60, 2)}:${pad(s % 60, 2)}`;
}
const INSTANT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(?:(Z)|([+-])(\d{2}):(\d{2}))$/;
/**
 * Strict ISO-8601 instant with a zone designator (`Z` or `±HH:MM`) → epoch ms, or null.
 * Fields are range-checked (no rollover); fractions are truncated to milliseconds.
 */
export function parseInstant(s) {
    const m = INSTANT_RE.exec(s);
    if (!m)
        return null;
    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    const h = +m[4];
    const mi = +m[5];
    const sec = m[6] === undefined ? 0 : +m[6];
    if (!validYmd(y, mo, d) || h > 23 || mi > 59 || sec > 59)
        return null;
    let ms = 0;
    if (m[7] !== undefined)
        ms = +(m[7] + '00').slice(0, 3);
    let off = 0;
    if (m[8] === undefined) {
        const oh = +m[10];
        const om = +m[11];
        if (oh > 23 || om > 59)
            return null;
        off = (oh * 60 + om) * 60000 * (m[9] === '-' ? -1 : 1);
    }
    return daysFromCivil(y, mo, d) * MS_PER_DAY + ((h * 60 + mi) * 60 + sec) * 1000 + ms - off;
}
/** Local wall-clock split of `instantMs` in `zone`: day number + ms after midnight. */
export function localParts(instantMs, zone) {
    const naive = wallClock(instantMs, zone);
    const day = Math.floor(naive / MS_PER_DAY);
    return { day, time: naive - day * MS_PER_DAY };
}
//# sourceMappingURL=time.js.map